import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Call, WhatsApp, Mail, PinDrop } from "@mui/icons-material";
import { motion } from "framer-motion";
import { companyInfo } from "../data/products";
import SEO from "../components/SEO";

const ContactContainer = styled(Container)(({ theme }) => ({
  marginTop: "2rem",
  marginBottom: "4rem",
}));

const FormCard = styled(Paper)(({ theme }) => ({
  padding: "2rem",
  borderRadius: "10px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
}));

const MapWrapper = styled(Box)(({ theme }) => ({
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  height: "500px",
  marginBottom: theme.spacing(3),
}));

const ContactInfo = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: "10px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 0),
  '&:last-child': {
    marginBottom: 0,
  }
}));

const ContactLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  minWidth: "80px",
  color: theme.palette.primary.main,
  marginRight: theme.spacing(2),
}));

const ContactValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  flex: 1,
}));

const QuoteCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  borderRadius: '10px',
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  textAlign: 'center',
  '& .MuiTypography-root': {
    color: '#ffffff',
  },
  '& .MuiButton-root': {
    margin: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: '#ffffff',
    },
  },
}));

const Section = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
  >
    {children}
  </motion.div>
);

// Replace with your Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby5vIeY_p6BntotP5PRi0w9vmYzwC55UwNCroKa5Rx4IVSOHo8zncRAprGVOQUQ9ZHcHg/exec';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitType, setSubmitType] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const saveToGoogleSheets = async (data) => {
    try {
      // eslint-disable-next-line no-unused-vars
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          ...data
        }),
        mode: 'no-cors'
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error saving to Google Sheets:', error);
      return { success: false, error: error.message };
    }
  };

  const handleWhatsApp = (includeFormData = false) => {
    let message = "Hi, I would like to get in touch regarding your products and services.";
    
    if (includeFormData && formData.name) {
      message = `Hi, I would like to get in touch regarding your products and services.\n\n`;
      message += `Name: ${formData.name}\n`;
      message += `Email: ${formData.email}\n`;
      message += `Phone: ${formData.phone}\n`;
      if (formData.company) message += `Company: ${formData.company}\n`;
      message += `Message: ${formData.message}`;
    }
    
    const whatsappUrl = `https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Save to Google Sheets
    const sheetResult = await saveToGoogleSheets(formData);
    
    // Open WhatsApp with form data
    handleWhatsApp(true);
    
    // Show success message
    setTimeout(() => {
      setIsSubmitting(false);
      if (sheetResult.success) {
        setSubmitMessage('Thank you for your message! We will get back to you soon. Your inquiry has also been sent via WhatsApp.');
        setSubmitType('success');
      } else {
        setSubmitMessage('Message sent via WhatsApp! There was an issue saving to our database, but we received your inquiry.');
        setSubmitType('warning');
      }
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    }, 1000);
  };

  // Generate Google Maps URL for the address
  const googleMapsUrl = 'https://maps.app.goo.gl/iqwmHbRwrEY9EQ5fA';

  return (
    <ContactContainer maxWidth="lg">
      <SEO
        title="Contact Shree Balaji Corporation"
        description="Contact Shree Balaji Corporation for PET straps, bale covers, leather washers, and strapping machines. Based in Akola, Maharashtra."
        keywords="contact, Shree Balaji Corporation, PET straps, bale covers, Akola, Maharashtra"
        url="/contact"
      />
      <Section>
        {/* Hero Banner */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
            textAlign: "center",
            py: 8,
            px: 4,
            borderRadius: 2,
            mb: 6,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "relative", zIndex: 2 }}>
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Contact Us
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.9 }}>
              Get in Touch with Shree Balaji Corporation
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Left Side - Map */}
          <Grid item xs={12} md={6}>
            <MapWrapper>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3732.636598691403!2d77.0647115!3d20.6843585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd72d0018308e27%3A0x2e8e6915b5352532!2sShree%20Balaji%20Corporation!5e0!3m2!1sen!2sin!4v1750595461264!5m2!1sen!2sin"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Shree Balaji Corporation Location"
              ></iframe>
            </MapWrapper>
          </Grid>

          {/* Right Side - Contact Form */}
          <Grid item xs={12} md={6}>
            <FormCard>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}>
                Send us a Message
              </Typography>

              {submitMessage && (
                <Alert severity={submitType} sx={{ mb: 3 }}>
                  {submitMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      placeholder="Tell us about your requirements..."
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        type="submit"
                        variant="outlined"
                        size="large"
                        disabled={isSubmitting}
                        startIcon={<WhatsApp />}
                        sx={{ flex: 1, position: 'relative' }}
                      >
                        {isSubmitting && (
                          <CircularProgress
                            size={20}
                            sx={{
                                flex: 1,
                                color: '#25D366', 
                                borderColor: '#25D366',
                                '&:hover': {
                                    borderColor: '#25D366',
                                    backgroundColor: 'rgba(37, 211, 102, 0.04)'
                                }
                            }}
                          />
                        )}
                        {isSubmitting ? 'Sending...' : ' Send Message via WhatsApp'}
                      </Button>
{/*                       
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => handleWhatsApp(false)}
                        disabled={isSubmitting}
                        sx={{ 
                          flex: 1,
                          color: '#25D366', 
                          borderColor: '#25D366',
                          '&:hover': {
                            borderColor: '#25D366',
                            backgroundColor: 'rgba(37, 211, 102, 0.04)'
                          }
                        }}
                      >
                        💬 WhatsApp Only
                      </Button> */}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </FormCard>
          </Grid>
        </Grid>

        {/* Bottom - Contact Information */}
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <ContactInfo>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: "primary.main", textAlign: "center" }}>
                Contact Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ContactItem>
                    {<Call />}<ContactLabel> Phone:</ContactLabel>
                    <ContactValue>
                      <Link href={`tel:${companyInfo.phone}`} color="inherit" underline="hover">
                        {companyInfo.phone}
                      </Link>
                      <br />
                      <Link href={`tel:${companyInfo.whatsapp}`} color="inherit" underline="hover">
                        {companyInfo.whatsapp}
                      </Link>
                    </ContactValue>
                  </ContactItem>

                  <ContactItem>
                    {<Mail />} <ContactLabel>Email:</ContactLabel>
                    <ContactValue>
                      <Link href={`mailto:${companyInfo.email}`} color="inherit" underline="hover">
                        {companyInfo.email}
                      </Link>
                    </ContactValue>
                  </ContactItem>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ContactItem>
                    {<WhatsApp />}<ContactLabel>WhatsApp:</ContactLabel>
                    <ContactValue>
                      <Link 
                        href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        color="inherit" 
                        underline="hover"
                      >
                        {companyInfo.whatsapp}
                      </Link>
                    </ContactValue>
                  </ContactItem>

                  <ContactItem>
                    {<PinDrop />}<ContactLabel> Address:</ContactLabel>
                    <ContactValue>
                      <Link 
                        href={googleMapsUrl}
                        target="_blank" 
                        rel="noopener noreferrer"
                        color="inherit" 
                        underline="hover"
                        sx={{ '&:hover': { color: 'primary.main' } }}
                      >
                        {companyInfo.address}
                      </Link>
                    </ContactValue>
                  </ContactItem>
                </Grid>
              </Grid>
            </ContactInfo>
          </Grid>
        </Grid>

        {/* Quote Section */}
        <Section>
          <QuoteCard>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Need a Quote?
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
              Get quick pricing for your requirements
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                href="/inquiry"
                variant="contained"
                size="large"
              >
                Request Quote
              </Button>
              <Button
                onClick={() => handleWhatsApp(false)}
                variant="contained"
                startIcon={<WhatsApp />}
                size="large"
                sx={{
                  backgroundColor: '#25D366',
                  '&:hover': {
                    backgroundColor: '#1EA952',
                  }
                }}
              >
                WhatsApp Quote
              </Button>
            </Box>
          </QuoteCard>
        </Section>
      </Section>
    </ContactContainer>
  );
};

export default ContactUs;
